import { Spinner } from '@brightlayer-ui/react-auth-workflow';
import { useOktaAuth } from '@okta/okta-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../app/hooks';
import { useLoginHandler } from '../../hooks/useLoginHandler';
import { Logger } from '../../logger';
import './LoginCallback.css';

export const LoginCallback: React.FC = () => {
    const { oktaAuth } = useOktaAuth();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const { handleLogin } = useLoginHandler(dispatch, navigate, oktaAuth);

    useEffect(() => {
        let mounted = true;

        const handleCallback = async (): Promise<void> => {
            if (!mounted) return;

            try {
                // Parse tokens from URL (Okta handles this automatically)
                await oktaAuth.handleLoginRedirect();

                // Get the tokens that were just set
                const token = await oktaAuth.tokenManager.get('accessToken');
                const accessToken = token && 'accessToken' in token ? token.accessToken : undefined;

                if (!accessToken) {
                    Logger.error('No access token received from Okta');
                    if (mounted) setError('Authentication failed. Please try again.');
                    navigate('/login');
                    return;
                }

                // Use shared login handler
                await handleLogin(accessToken);
            } catch (err) {
                Logger.error(`Login callback error: ${String(err)}`);
                if (mounted) setError('An error occurred during login. Please try again.');
                navigate('/login');
            }
        };

        void handleCallback();

        return (): void => {
            mounted = false;
        };
    }, [oktaAuth, handleLogin, navigate]);

    return (
        <div className="login-callback-container">
            <Spinner visible={!error} />
            {error && (
                <div className="login-callback-error">
                    <p>{error}</p>
                    <p>Redirecting to login...</p>
                </div>
            )}
        </div>
    );
};
